/* eslint-disable max-classes-per-file */
/* eslint-disable no-useless-constructor */
import { CONCEPTS } from '@rover/rsdk/src/concepts';
import type { ConsentPurpose } from '@rover/rsdk/src/modules/Analytics';
import { AnalyticsEvent } from '@rover/types/src/Analytics';

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a provider successfully saves one of the profile modules under
 * `/provider-profile/details/` -- experience, cat experience, skills, or what
 * to expect. Paired with `provider-account-info-updated` and
 * `provider-payout-info-updated` to measure completion of the management hub's
 * modules.
 *
 * &nbsp;
 */
export class ProviderProfileInfoUpdated extends AnalyticsEvent {
  eventName = 'provider-profile-info-updated';

  consentPurpose: ConsentPurpose = 'performance';

  concept = CONCEPTS.service_offerings.provider_profile;

  constructor(eventProperties: { provider_status: string; module: string }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a provider skips one of the optional goals questions in sign up,
 * naming the question they skipped. Every question stores an empty answer when
 * unanswered, so the table cannot tell a deliberate skip from a question never
 * reached -- this event is what separates the two. `had_answer` separates them
 * again within the skips: someone paging back through answers they already gave
 * emits a skip per question, which is not the declaration of intent that
 * declining to answer is.
 *
 * &nbsp;
 */
export class ProviderProfileGoalsQuestionSkipped extends AnalyticsEvent {
  eventName = 'provider-profile-goals-question-skipped';

  consentPurpose: ConsentPurpose = 'performance';

  concept = CONCEPTS.service_offerings.provider_profile;

  constructor(eventProperties: {
    provider_status: string;
    person_opk: string;
    question: string;
    had_answer: boolean;
  }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when one of the optional goals questions in sign up comes into view,
 * naming the question. Paired with the skip and answer events, it separates a
 * question someone reached and left from one they never reached, and gives each
 * question's response rate its denominator. The work setup follow-up reports
 * when it appears, since only some work situations ask it. `had_answer` marks a
 * revisit of a question already answered.
 *
 * &nbsp;
 */
export class ProviderProfileGoalsQuestionViewed extends AnalyticsEvent {
  eventName = 'provider-profile-goals-question-viewed';

  consentPurpose: ConsentPurpose = 'performance';

  concept = CONCEPTS.service_offerings.provider_profile;

  constructor(eventProperties: {
    provider_status: string;
    person_opk: string;
    question: string;
    had_answer: boolean;
  }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires once per optional goals question answered in sign up, carrying the
 * response itself so the answers are readable from the event stream rather than
 * only from the module they are saved into. `question` is a fixed slug, not the
 * wording shown, so rewording a question does not split its history; `answer`
 * is the stored enum value for the same reason. `answered_at` is stamped where
 * the provider answered, since the last question's event is replayed on the
 * following page and arrives after the fact.
 *
 * Questions submitted blank emit nothing: an empty answer is what an unreached
 * question stores too, so it is no evidence of a response. A deliberate pass
 * is `provider-profile-goals-question-skipped` instead.
 *
 * &nbsp;
 */
export class ProviderProfileGoalsQuestionAnswered extends AnalyticsEvent {
  eventName = 'provider-profile-goals-question-answered';

  consentPurpose: ConsentPurpose = 'performance';

  concept = CONCEPTS.service_offerings.provider_profile;

  constructor(eventProperties: {
    provider_status: string;
    person_opk: string;
    question: string;
    answer: string;
    answered_at: string;
    had_answer: boolean;
  }) {
    super(eventProperties);
  }
}
